import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'

import { userRefreshed } from '../app/authSlice'
import { useAppDispatch, useAppSelector } from '../app/store'
import { authApi } from '../services/endpoints'
import type { UserInfo } from '../types'

import { useLiveRefresh } from './useLiveRefresh'

/** بصمة ما يحدّد وصول المستخدم — الترتيب لا يهم */
const accessOf = (u: UserInfo) =>
  JSON.stringify([
    [...u.roles].sort(),
    [...(u.menuPermissions ?? [])].sort(),
    u.employeeId ?? null,
    u.fullNameAr,
  ])

/**
 * مزامنة صلاحيات الجلسة المفتوحة: الأدوار والصفحات تُقرأ من الخادم عند فتح النظام، وفور تعديلها
 * من «إدارة المستخدمين» (حدث my-access)، وعند العودة للتبويب — فالقائمة الجانبية وحارس الصفحات
 * يعكسان آخر ما حدّده المدير بلا خروج ودخول.
 */
export function useAccessSync() {
  const dispatch = useAppDispatch()
  const user = useAppSelector((s) => s.auth.user)
  const userRef = useRef(user)
  useLayoutEffect(() => {
    userRef.current = user
  })

  const sync = useCallback(async () => {
    try {
      const fresh = await authApi.me()
      const current = userRef.current
      if (!current || fresh.id !== current.id) return
      if (accessOf(fresh) !== accessOf(current)) dispatch(userRefreshed({ ...current, ...fresh }))
    } catch {
      // الشبكة أو الخادم — تبقى الجلسة على آخر صلاحيات معروفة
    }
  }, [dispatch])

  useEffect(() => {
    void sync()
  }, [sync])
  useLiveRefresh(['my-access'], () => void sync())
  // العودة للتبويب (أو للنافذة): جلسةٌ مفتوحة منذ أيام تلتقط ما مُنح أثناء غيابها عن الاتصال الحيّ
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void sync()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [sync])
}
